const API = ("" || "") + "/api/admin";

async function req(path, opts = {}) {
  const res = await fetch(`${API}${path}`, {
    credentials: "include",
    headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (res.status === 401) {
    // Tenta extrair mensagem específica (ex.: "Email ou palavra-passe
    // incorretos"). Só cai no genérico "unauthenticated" se o body não
    // for JSON parseável — usado por session-check em `/auth/me`.
    let detail = "unauthenticated";
    try {
      const j = await res.clone().json();
      if (j && (j.detail || j.message)) detail = j.detail || j.message;
    } catch (e) { /* keep default */ }
    const err = new Error(detail); err.status = 401; throw err;
  }
  if (!res.ok) {
    let msg = "Erro";
    try {
      const j = await res.clone().json();
      msg = j.detail || j.message || msg;
    } catch (e) {
      // non-JSON body — pode ser página HTML do proxy/CDN (502/504),
      // Cloudflare block ou WAF. Detectamos e mostramos mensagem amigável.
      try {
        const t = (await res.text()).trim();
        const looksLikeHtml = t.startsWith("<") || t.toLowerCase().includes("<!doctype") || t.toLowerCase().includes("<html");
        if (looksLikeHtml) {
          if (res.status === 502 || res.status === 503 || res.status === 504) {
            msg = "Servidor temporariamente indisponível. Aguarde uns segundos e tente novamente.";
          } else if (res.status === 403) {
            msg = "Acesso bloqueado pelo servidor. Verifique credenciais ou permissões.";
          } else {
            msg = `Erro do gateway (${res.status}). Tente novamente.`;
          }
        } else if (t) {
          msg = t.slice(0, 120);
        }
      } catch (e2) { /* ignore */ }
    }
    if (msg === "Erro" || !msg) msg = `Erro ${res.status}`;
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return res.json();
}

export const adminApi = {
  login: (email, password) => req("/auth/login", { method: "POST", body: JSON.stringify({ email, password }) }),
  me: () => req("/auth/me"),
  logout: () => req("/auth/logout", { method: "POST" }),

  listTables: () => req("/tables"),
  tableSchema: (schema, name) => req(`/tables/${schema}/${name}/schema`),
  tableList: (schema, name, body) => req(`/tables/${schema}/${name}/list`, { method: "POST", body: JSON.stringify(body) }),
  tableGet: (schema, name, pk) => req(`/tables/${schema}/${name}/${encodeURIComponent(pk)}`),
  tableCreate: (schema, name, values) => req(`/tables/${schema}/${name}`, { method: "POST", body: JSON.stringify({ values }) }),
  tableUpdate: (schema, name, pk, values) => req(`/tables/${schema}/${name}/${encodeURIComponent(pk)}`, { method: "PUT", body: JSON.stringify({ values }) }),
  tableRelated: (schema, name) => req(`/tables/${schema}/${name}/_related`),
  tableLookup: (schema, name, column, { q = "", ids = "", limit = 25 } = {}) => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (ids) params.set("ids", ids);
    if (limit) params.set("limit", String(limit));
    const qs = params.toString();
    return req(`/tables/${schema}/${name}/_lookup/${encodeURIComponent(column)}${qs ? `?${qs}` : ""}`);
  },

  // Consultant renewals listing (Dashboard → Listagens)
  consultantRenewals: ({ year, quarter, q } = {}) => {
    const params = new URLSearchParams();
    if (year) params.set("year", String(year));
    if (quarter) params.set("quarter", String(quarter));
    if (q) params.set("q", q);
    const qs = params.toString();
    return req(`/dashboard/consultant-renewals${qs ? `?${qs}` : ""}`);
  },
  quarterlyAwards: (year) => req(`/dashboard/quarterly-awards${year ? `?year=${year}` : ""}`),

  // Manual password reset link generator (admin fallback for clients
  // who can't receive the automated email — corporate spam, etc.).
  generateResetLink: (clientId) => req(`/ops/generate-reset-link/${clientId}`, { method: "POST" }),
  setClientPassword: (clientId, newPassword) =>
    req(`/ops/set-password/${clientId}`, {
      method: "POST",
      body: JSON.stringify({ new_password: newPassword }),
    }),
  listInscricoesPendentes: () => req(`/ops/inscricoes-pendentes`),
  forceSendInscricao: (linkId) =>
    req(`/ops/inscricoes-pendentes/force-send`, {
      method: "POST",
      body: JSON.stringify({ link_id: linkId }),
    }),

  // Dashboard / orders workflow
  ordersSummary: () => req("/dashboard/orders-summary"),
  ordersByState: (state, { page = 1, page_size = 25, search = "" } = {}) => {
    const p = new URLSearchParams({ state, page, page_size });
    if (search) p.set("search", search);
    return req(`/dashboard/orders?${p.toString()}`);
  },
  ordersQuickSearch: ({ order_id = "", client_id = "", invoice_id = "", email = "" } = {}) => {
    const p = new URLSearchParams({ page: 1, page_size: 50 });
    if (order_id) p.set("order_id", String(order_id));
    if (client_id) p.set("client_id", String(client_id));
    if (invoice_id) p.set("invoice_id", String(invoice_id));
    if (email) p.set("email", String(email));
    return req(`/dashboard/orders?${p.toString()}`);
  },
  orderStates: () => req("/dashboard/order-states"),
  orderTransition: (body) => req("/dashboard/order-transition", { method: "POST", body: JSON.stringify(body) }),
  orderMessage: (body) => req("/dashboard/order-message", { method: "POST", body: JSON.stringify(body) }),
  orderBilling: (orderId) => req(`/dashboard/order-billing/${orderId}`),
  orderEmitDocument: (body) => req("/dashboard/order-emit-document", { method: "POST", body: JSON.stringify(body) }),
  orderRecalcCommissions: (body) => req("/dashboard/order-recalc-commissions", { method: "POST", body: JSON.stringify(body) }),
  orderResendPrEmail: (orderId) => req(`/dashboard/order-resend-pr-email/${orderId}`, { method: "POST" }),
  orderRefreshCofidisLink: (orderId, sendEmail = true) => req(`/dashboard/order-refresh-cofidis-link/${orderId}?send_email_to_client=${sendEmail}`, { method: "POST" }),
  stuckPaymentUrls: (minutes = 15) => req(`/dashboard/stuck-payment-urls?minutes=${minutes}`),
  regeneratePaymentUrl: (orderId) => req("/dashboard/regenerate-payment-url", { method: "POST", body: JSON.stringify({ order_id: orderId }) }),
  reconcilePayment: (orderId) => req("/dashboard/reconcile-payment", { method: "POST", body: JSON.stringify({ order_id: orderId }) }),
  cancelStuckOrder: (orderId) => req("/dashboard/cancel-stuck-order", { method: "POST", body: JSON.stringify({ order_id: orderId }) }),
  ignoreStuckOrder: (orderId, reason) => req("/dashboard/ignore-stuck-order", { method: "POST", body: JSON.stringify({ order_id: orderId, reason }) }),

  // Avaliação de Marketing
  marketingEvaluation: (year, month = 0) => req(`/marketing/evaluation?year=${year}&month=${month}`),
  marketingEvolution: (year) => req(`/marketing/evolution?year=${year}`),
  marketingExportUrl: (year, month = 0) => `${API}/marketing/export?year=${year}&month=${month}`,
  marketingSaveCampaignCost: (body) => req("/marketing/campaign-costs", { method: "POST", body: JSON.stringify(body) }),
  marketingSaveMonthlyCost: (body) => req("/marketing/monthly-costs", { method: "POST", body: JSON.stringify(body) }),
  marketingMetaCampaigns: (year, month = 0) => req(`/marketing/meta/campaigns?year=${year}&month=${month}`),
  marketingMetaMap: (body) => req("/marketing/meta/map", { method: "POST", body: JSON.stringify(body) }),  marketingMetaSync: (days_back = 3) => req("/marketing/meta/sync", { method: "POST", body: JSON.stringify({ days_back }) }),
  marketingAttribution: (year, month = 0) => req(`/marketing/attribution?year=${year}&month=${month}`),
  marketingAttributionRebuild: () => req("/marketing/attribution/rebuild", { method: "POST" }),
  marketingCheckoutFunnel: (year, month = 0) => req(`/marketing/checkout-funnel?year=${year}&month=${month}`),
  marketingCheckoutFunnelCleanup: (body) => req("/marketing/checkout-funnel/cleanup-false-shipping-blocks", { method: "POST", body: JSON.stringify(body || {}) }),
  paymentWebhooksHealth: () => req("/dashboard/payment-webhooks-health"),

  auditLog: (limit = 100) => req(`/audit?limit=${limit}`),

  // Operations
  pick: (entity, q = "", limit = 25) => {
    const p = new URLSearchParams({ q, limit });
    return req(`/ops/pick/${entity}?${p.toString()}`);
  },
  opsClientParent: (id) => req(`/ops/client-parent?id=${id}`),
  opsChangeParent: (body) => req(`/ops/change-parent`, { method: "POST", body: JSON.stringify(body) }),
  opsCopyProduct: (body) => req(`/ops/copy-product`, { method: "POST", body: JSON.stringify(body) }),
  opsPaymentTypes: () => req(`/ops/payment-types`),
  opsCommissionPosition: (clientId) => req(`/ops/commission-position/${clientId}`),
  opsRegisterCommissionPayment: (body) => req(`/ops/register-commission-payment`, { method: "POST", body: JSON.stringify(body) }),
  opsCommissionsPayable: (params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") p.set(k, v);
    });
    const qs = p.toString();
    return req(`/ops/commissions-payable${qs ? `?${qs}` : ""}`);
  },
  opsRegisterCommissionPaymentsBatch: (body) => req(`/ops/register-commission-payments-batch`, { method: "POST", body: JSON.stringify(body) }),
  opsManualCommissionAdjustment: (body) => req(`/ops/manual-commission-adjustment`, { method: "POST", body: JSON.stringify(body) }),
  opsConfirmClientsPreview: () => req(`/ops/confirm-clients/preview`),
  opsConfirmClients: (body) => req(`/ops/confirm-clients`, { method: "POST", body: JSON.stringify(body) }),

  // Importação em massa de clientes (associados a um patrocinador)
  importClientsResolveSponsor: (email) =>
    req(`/ops/import-clients/resolve-sponsor?email=${encodeURIComponent(email)}`),
  importClientsStatus: (jobId) => req(`/ops/import-clients/status/${jobId}`),
  importClientsTemplateUrl: () => `${API}/ops/import-clients/template`,
  importClients: (file, sponsorEmail, dryRun = true) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("sponsor_email", sponsorEmail);
    fd.append("dry_run", dryRun ? "true" : "false");
    return fetch(`${API}/ops/import-clients`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro ao importar clientes"); }
      return r.json();
    });
  },

  // Reports (all share pagination)
  report: (slug, params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") p.set(k, v);
    });
    const qs = p.toString();
    return req(`/ops/report/${slug}${qs ? `?${qs}` : ""}`);
  },

  // Media Center Summaries (Whisper + Gemini 3 Flash pipeline)
  summaryList: (params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") p.set(k, v);
    });
    const qs = p.toString();
    return req(`/media-summary/list${qs ? `?${qs}` : ""}`);
  },
  summaryGet: (id) => req(`/media-summary/${id}`),
  summaryEdit: (id, payload) => req(`/media-summary/edit/${id}`, { method: "PUT", body: JSON.stringify(payload) }),
  summaryGenerate: (id, force = false) => req(`/media-summary/generate/${id}${force ? "?force=true" : ""}`, { method: "POST" }),
  summaryBackfillStart: (params = {}) => {
    const p = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") p.set(k, v);
    });
    const qs = p.toString();
    return req(`/media-summary/backfill/start${qs ? `?${qs}` : ""}`, { method: "POST" });
  },
  summaryBackfillStatus: () => req(`/media-summary/backfill/status`),
  summaryBackfillStop: () => req(`/media-summary/backfill/stop`, { method: "POST" }),

  // Preçários B2B (preçário 2026 e versionamento por ano)
  precarioList: () => req(`/precarios`),
  precarioGet: (id) => req(`/precarios/${id}`),
  precarioCreate: (body) => req(`/precarios`, { method: "POST", body: JSON.stringify(body) }),
  precarioUpdate: (id, body) => req(`/precarios/${id}`, { method: "PATCH", body: JSON.stringify(body) }),
  precarioActivate: (id) => req(`/precarios/${id}/activate`, { method: "POST" }),
  precarioItemCreate: (id, body) => req(`/precarios/${id}/items`, { method: "POST", body: JSON.stringify(body) }),
  precarioItemUpdate: (id, idItem, body) => req(`/precarios/${id}/items/${idItem}`, { method: "PATCH", body: JSON.stringify(body) }),
  precarioItemDelete: (id, idItem) => req(`/precarios/${id}/items/${idItem}`, { method: "DELETE" }),
  precarioDescontoCreate: (id, body) => req(`/precarios/${id}/descontos`, { method: "POST", body: JSON.stringify(body) }),
  precarioDescontoUpdate: (id, idDesc, body) => req(`/precarios/${id}/descontos/${idDesc}`, { method: "PATCH", body: JSON.stringify(body) }),
  precarioDescontoDelete: (id, idDesc) => req(`/precarios/${id}/descontos/${idDesc}`, { method: "DELETE" }),
  precarioLookupProdutos: (q = "") => {
    const qs = q ? `?q=${encodeURIComponent(q)}` : "";
    return req(`/precarios/_lookup/produtos${qs}`);
  },

  // Cursos — Inscrições e Templates (Sprint 1A)
  cursosLookups: () => req(`/cursos/lookups`),
  cursoClienteLookup: ({ email, nif } = {}) => {
    const qs = new URLSearchParams();
    if (email) qs.set("email", email);
    if (nif) qs.set("nif", nif);
    return req(`/cursos/lookup/cliente?${qs.toString()}`);
  },
  cursoEncomendaLookup: (id) => req(`/cursos/lookup/encomenda/${encodeURIComponent(id)}`),
  cursoInscricoesList: (cursoId) => req(`/cursos/${cursoId}/inscricoes`),
  cursoNotasList: (cursoId) => req(`/cursos/${cursoId}/notas`),
  cursoNotaSet: (cursoId, fichaId, nota) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/nota`, {
      method: "PUT",
      body: JSON.stringify({ nota }),
    }),
  cursoAvaliacaoSet: (cursoId, fichaId, patch) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/avaliacao`, {
      method: "PUT",
      body: JSON.stringify(patch),
    }),
  cursoAvaliacaoObsGlobalSet: (cursoId, observacoes) =>
    req(`/cursos/${cursoId}/avaliacao/observacoes-global`, {
      method: "PUT",
      body: JSON.stringify({ observacoes }),
    }),
  cursoDtpData: (cursoId) => req(`/cursos/${cursoId}/dtp/data`),
  cursoDtpDataSave: (cursoId, values) =>
    req(`/cursos/${cursoId}/dtp/data`, { method: "PUT", body: JSON.stringify(values) }),
  cursoDtpRegistry: (cursoId) => req(`/cursos/${cursoId}/dtp/registry`),
  cursoDtpAudit: (cursoId) => req(`/cursos/${cursoId}/dtp/audit`),
  cursoDtpGenerateUrl: (cursoId) => `${API}/cursos/${cursoId}/dtp/generate`,
  cursoDtpPreviewUrl: (cursoId, chapterId) => `${API}/cursos/${cursoId}/dtp/preview/${chapterId}`,
  cursoDtpUploadStatus: (cursoId, tipo) => req(`/cursos/${cursoId}/dtp/uploads/${tipo}`),
  cursoDtpUploadUrl: (cursoId, tipo) => `${API}/cursos/${cursoId}/dtp/uploads/${tipo}`,
  cursoDtpUploadDelete: (cursoId, tipo) =>
    req(`/cursos/${cursoId}/dtp/uploads/${tipo}`, { method: "DELETE" }),
  cursoDtpArquivar: (cursoId) =>
    req(`/cursos/${cursoId}/dtp/arquivar`, { method: "POST", body: "{}" }),
  cursoDtpArquivoGet: (cursoId) => req(`/cursos/${cursoId}/dtp/arquivo`),

  cursoPresencasMatrix: (cursoId) => req(`/cursos/${cursoId}/presencas`),
  cursoPresencaToggle: (cursoId, fichaId, ordem, presente) =>
    req(`/cursos/${cursoId}/presencas/${fichaId}/${ordem}`, {
      method: "PUT",
      body: JSON.stringify({ presente }),
    }),
  cursoCompras: (cursoId) => req(`/cursos/${cursoId}/compras`),
  cursoBulkFromCompras: (cursoId, items, sendEmail = true) =>
    req(`/cursos/${cursoId}/inscricoes/bulk-from-compras`, {
      method: "POST",
      body: JSON.stringify({ items, send_email: sendEmail }),
    }),
  cursoBulkFromExcel: (cursoId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${API}/cursos/${cursoId}/inscricoes/bulk-from-excel`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro a importar Excel"); }
      return r.json();
    });
  },
  cursoExcelTemplateUrl: () => `${API}/cursos/templates/fichas-excel/download`,
  cursoExportSigoUrl: (cursoId) => `${API}/cursos/${cursoId}/inscricoes/export-sigo`,

  incentivosList: () => req('/incentivos'),
  incentivosCreate: (body) => req('/incentivos', { method: 'POST', body: JSON.stringify(body) }),
  incentivosUpdate: (id, body) => req(`/incentivos/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  incentivosDelete: (id) => req(`/incentivos/${id}`, { method: 'DELETE' }),
  incentivosLeaderboard: (id) => req(`/incentivos/${id}/leaderboard`),
  incentivosPopupGet: () => req('/incentivos/popup-settings'),
  incentivosPopupSet: (body) => req('/incentivos/popup-settings', { method: 'PUT', body: JSON.stringify(body) }),

  // Transferência de vales entre clientes
  valesSearch: (q) => req(`/vales/search?q=${encodeURIComponent(q)}`),
  valesCliente: (id) => req(`/vales/cliente/${id}`),
  valesTransfer: (body) => req('/vales/transfer', { method: 'POST', body: JSON.stringify(body) }),
  valesHistorico: () => req('/vales/historico'),
  incentivosUploadImage: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${API}/incentivos/upload-image`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro a carregar imagem"); }
      return r.json();
    });
  },
  blackMonthGet: () => req('/black-month'),
  blackMonthUpdate: (body) => req('/black-month', { method: 'PUT', body: JSON.stringify(body) }),

  // Sprint 1D — Avaliação da Reação do Formando
  cursoAvaliacaoStatus: (cursoId) => req(`/cursos/${cursoId}/avaliacao/status`),
  cursoAvaliacaoResumo: (cursoId) => req(`/cursos/${cursoId}/avaliacao/resumo`),
  cursoAvaliacaoFechar: (cursoId) => req(`/cursos/${cursoId}/avaliacao/fechar`, { method: "POST" }),
  cursoAvaliacaoReabrir: (cursoId) => req(`/cursos/${cursoId}/avaliacao/reabrir`, { method: "POST" }),
  cursoAvaliacaoEnviarEmails: (cursoId) => req(`/cursos/${cursoId}/avaliacao/enviar-emails`, { method: "POST" }),

  // Mod. 16 — Formadores do curso + Avaliação de Reação do Formador
  cursoFormadoresList: (cursoId) => req(`/cursos/${cursoId}/formadores`),
  cursoFormadoresAvailable: (cursoId, q = "") =>
    req(`/cursos/${cursoId}/formadores/available${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  cursoFormadorAdd: (cursoId, idFormador) =>
    req(`/cursos/${cursoId}/formadores`, {
      method: "POST", body: JSON.stringify({ id_formador: idFormador }),
    }),
  cursoFormadorUpdate: (cursoId, fid, body) =>
    req(`/cursos/${cursoId}/formadores/${fid}`, { method: "PATCH", body: JSON.stringify(body) }),
  cursoFormadorDelete: (cursoId, fid) =>
    req(`/cursos/${cursoId}/formadores/${fid}`, { method: "DELETE" }),
  cursoAvaliacaoFormadorList: (cursoId) => req(`/cursos/${cursoId}/avaliacao-formador`),
  cursoAvaliacaoFormadorQuestions: (cursoId) => req(`/cursos/${cursoId}/avaliacao-formador/questions`),
  cursoAvaliacaoFormadorSet: (cursoId, fid, body) =>
    req(`/cursos/${cursoId}/avaliacao-formador/${fid}`, {
      method: "PUT", body: JSON.stringify(body),
    }),
  cursoAvaliacaoFormadorEnviarEmails: (cursoId, forceResend = false) =>
    req(`/cursos/${cursoId}/avaliacao-formador/enviar-emails`, {
      method: "POST", body: JSON.stringify({ force_resend: forceResend }),
    }),
  cursoAvaliacaoFormadorReenviar: (cursoId, fid) =>
    req(`/cursos/${cursoId}/avaliacao-formador/${fid}/reenviar`, { method: "POST" }),

  // Contratos (Secção 10)
  contratosSnapshot: (cursoId) => req(`/cursos/${cursoId}/contratos`),
  contratosEmpresaDadosPatch: (cursoId, body) =>
    req(`/cursos/${cursoId}/contratos/empresa/dados`, { method: "PATCH", body: JSON.stringify(body) }),
  contratosEmpresaGerar: (cursoId) =>
    req(`/cursos/${cursoId}/contratos/empresa/gerar`, { method: "POST" }),
  contratosFormadorGerar: (cursoId, fid) =>
    req(`/cursos/${cursoId}/contratos/formadores/${fid}/gerar`, { method: "POST" }),

  // Manutenção Global de Formadores (master)
  formadoresList: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.activo) qs.set("activo", params.activo);
    if (params.limit) qs.set("limit", params.limit);
    return req(`/formadores${qs.toString() ? `?${qs}` : ""}`);
  },
  formadorGet: (fid) => req(`/formadores/${fid}`),
  formadorCreate: (body) => req(`/formadores`, { method: "POST", body: JSON.stringify(body) }),
  formadorUpdate: (fid, body) => req(`/formadores/${fid}`, { method: "PATCH", body: JSON.stringify(body) }),
  formadorDelete: (fid) => req(`/formadores/${fid}`, { method: "DELETE" }),
  formadorSearchCliente: (q, limit = 15) =>
    req(`/formadores/search-cliente?q=${encodeURIComponent(q)}&limit=${limit}`),

  // Sprint 1F — Validação + Check-in
  cursoValidar: (cursoId, fichaId) => req(`/cursos/${cursoId}/inscricoes/${fichaId}/validar`, { method: "POST" }),
  cursoCheckinLookup: (code) => req(`/cursos/checkin/lookup?code=${encodeURIComponent(code)}`),
  cursoCheckinRegister: (fichaId, ordem) => req(`/cursos/checkin/register`, {
    method: "POST", body: JSON.stringify({ ficha_id: fichaId, ordem }),
  }),
  cursoCheckinList: (cursoId) => req(`/cursos/${cursoId}/checkin/list`),
  cursoCheckinSummary: (cursoId) => req(`/cursos/${cursoId}/checkin/summary`),
  cursoCdnFiles: (cursoId) => req(`/cursos/${cursoId}/cdn-files`),
  cursoCdnFileDelete: (cursoId, name) =>
    req(`/cursos/${cursoId}/cdn-files/${encodeURIComponent(name)}`, { method: "DELETE" }),
  // URL do PNG do QR (mesmo endpoint usado pela página de check-in operador).
  // Mantemos relativo para garantir same-origin (importante em iOS Safari).
  cursoCheckinQrUrl: (code) => `/api/admin/cursos/checkin/barcode/${encodeURIComponent(code)}`,

  // Operadores de Check-in (por curso)
  cursoCheckinOpsList: (cursoId) => req(`/cursos/${cursoId}/checkin/operadores`),
  cursoCheckinOpsAdd: (cursoId, idCliente) => req(`/cursos/${cursoId}/checkin/operadores`, {
    method: "POST", body: JSON.stringify({ id_cliente: idCliente }),
  }),
  cursoCheckinOpsRemove: (cursoId, idCliente) => req(`/cursos/${cursoId}/checkin/operadores/${idCliente}`, { method: "DELETE" }),
  cursoCheckinClientesSearch: (q) => req(`/cursos/checkin/clientes/search?q=${encodeURIComponent(q)}`),
  cursoInscricaoGet: (cursoId, fichaId) => req(`/cursos/${cursoId}/inscricoes/${fichaId}`),
  cursoInscricaoCreate: (cursoId, payload) => req(`/cursos/${cursoId}/inscricoes`, { method: "POST", body: JSON.stringify(payload) }),
  cursoInscricaoUpdate: (cursoId, fichaId, payload) => req(`/cursos/${cursoId}/inscricoes/${fichaId}`, { method: "PUT", body: JSON.stringify(payload) }),
  cursoInscricaoDelete: (cursoId, fichaId) => req(`/cursos/${cursoId}/inscricoes/${fichaId}`, { method: "DELETE" }),
  cursoFichasIncompletasComUploads: (cursoId) => req(`/cursos/${cursoId}/fichas-incompletas-com-uploads`),
  cursoInscricaoSetRepetente: (cursoId, fichaId, value) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/repetente`, {
      method: "PATCH",
      body: JSON.stringify({ value: !!value }),
    }),
  // ── Diplomas (Sprint 1H) ───────────────────────────────────────────
  // Preview devolve PDF binário inline — usar abrindo o URL directamente
  // numa nova aba: `${API}/cursos/.../diploma/preview` via fetch+blob.
  diplomaPreview: (cursoId, fichaId) =>
    fetch(`${API}/cursos/${cursoId}/inscricoes/${fichaId}/diploma/preview`, {
      method: "POST", credentials: "include",
    }).then(async (r) => {
      if (!r.ok) {
        let msg = "Erro";
        try { const j = await r.clone().json(); msg = j.detail || msg; } catch { /* ignore */ }
        throw new Error(msg);
      }
      return r.blob();
    }),
  diplomaEnviar: (cursoId, fichaId) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/diploma/enviar`, { method: "POST" }),
  diplomaEnviarEmMassa: (cursoId, opts = {}) =>
    req(`/cursos/${cursoId}/diplomas/enviar-em-massa`, {
      method: "POST",
      body: JSON.stringify(opts),
    }),
  cursoTemplatesList: () => req(`/cursos/templates`),
  cursoTemplateDownloadUrl: (tipo) => `${API}/cursos/templates/${tipo}/download`,
  cursoTemplatePreviewUrl: (tipo) => `${API}/cursos/templates/${tipo}/preview`,
  cursoTemplateUpload: (tipo, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${API}/cursos/templates/${tipo}/upload`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro upload"); }
      return r.json();
    });
  },
  // List active courses for the picker — devolve também `Descricao` do produto
  cursosListActive: () => req(`/cursos/list?only_active=true&limit=300`),
  cursosSituacao: (qs = "") => req(`/cursos/situacao${qs ? `?${qs}` : ""}`),
  cursoCopiarPreview: (id) => req(`/cursos/copiar/preview?curso=${id}`),
  cursoCopiarDiff: (origem, destino) => req(`/cursos/copiar/diff?origem=${origem}&destino=${destino}`),
  cursoCopiar: (origem, destino, overwrite) => req(`/cursos/copiar`, { method: "POST", body: JSON.stringify({ origem, destino, overwrite }) }),
  cursosListIncludingPast: () => req(`/cursos/list?only_active=true&include_past=true&limit=500`),

  // Sprint 1C — Geração de documentos preenchidos + upload de PDF assinado
  // (Jun/2026: Ficha/Contrato/Menor passaram a PDF Reportlab via `cursoDocumentoPdfUrl`).
  cursoAssinadoDownloadUrl: (cursoId, fichaId, tipo) =>
    `${API}/cursos/${cursoId}/inscricoes/${fichaId}/assinado/${tipo}/download`,
  cursoAssinadoUpload: (cursoId, fichaId, tipo, file) => {
    // Aceita 1 File ou um array com 2 PDFs (backend faz merge automaticamente).
    const fd = new FormData();
    const arr = Array.isArray(file) ? file : [file];
    arr.forEach((f) => fd.append("files", f));
    return fetch(`${API}/cursos/${cursoId}/inscricoes/${fichaId}/assinado/${tipo}/upload`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro upload"); }
      return r.json();
    });
  },
  cursoAssinadoDelete: (cursoId, fichaId, tipo) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/assinado/${tipo}`, { method: "DELETE" }),

  // Sprint 1G — Confirmação manual de recepção por email (sem upload)
  cursoConfirmarEmail: (cursoId, fichaId, tipo, confirmado = true) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/confirmar-email/${tipo}`, {
      method: "POST",
      body: JSON.stringify({ confirmado }),
    }),

  // Sprint 1B — Marcar Inscrito + envio email link público
  cursoMarcarInscrito: (cursoId, encId) =>
    req(`/cursos/${cursoId}/encomendas/${encId}/marcar-inscrito`, { method: "POST" }),
  cursoLinkInscricao: (cursoId, encId) =>
    req(`/cursos/${cursoId}/encomendas/${encId}/link-inscricao`),
  cursoRegenerarLink: (cursoId, encId) =>
    req(`/cursos/${cursoId}/encomendas/${encId}/regenerar-link`, { method: "POST" }),
  cursoEnviarLembretes: (cursoId) =>
    req(`/cursos/${cursoId}/enviar-lembretes`, { method: "POST" }),
  cursoResendEmail: (cursoId, fichaId) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/resend-email`, { method: "POST" }),
  cursoInscricaoAudit: (cursoId, fichaId) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/audit`),
  cursoDocumentoPdfUrl: (cursoId, fichaId, tipo) =>
    `${API}/cursos/${cursoId}/inscricoes/${fichaId}/documento/${tipo}/pdf`,

  // Sessões pedagógicas (Trel_PlanoSessao estendida)
  sessoesList: (cursoId) => req(`/cursos/${cursoId}/sessoes`),
  sessoesGet: (cursoId, sessaoId) => req(`/cursos/${cursoId}/sessoes/${sessaoId}`),
  sessoesCreate: (cursoId, payload) =>
    req(`/cursos/${cursoId}/sessoes`, { method: "POST", body: JSON.stringify(payload) }),
  sessoesUpdate: (cursoId, sessaoId, payload) =>
    req(`/cursos/${cursoId}/sessoes/${sessaoId}`, { method: "PUT", body: JSON.stringify(payload) }),
  sessoesDelete: (cursoId, sessaoId) =>
    req(`/cursos/${cursoId}/sessoes/${sessaoId}`, { method: "DELETE" }),
  sessoesShiftDates: (cursoId, newDataInicio) =>
    req(`/cursos/${cursoId}/sessoes/shift-dates`, {
      method: "POST",
      body: JSON.stringify({ new_data_inicio: newDataInicio }),
    }),
  sessoesImportFromCurso: (cursoId, { sourceCursoId, replace = false, shiftStart = null } = {}) =>
    req(`/cursos/${cursoId}/sessoes/import-from-curso`, {
      method: "POST",
      body: JSON.stringify({
        source_curso_id: sourceCursoId,
        replace,
        shift_start: shiftStart,
      }),
    }),
  sessoesTemplateUrl: () => `${API}/cursos/sessoes/template/download`,
  cursoPresencasPdfUrl: (cursoId) => `${API}/cursos/${cursoId}/presencas-pdf`,
  cursoIncompletosPdfUrl: (cursoId) => `${API}/cursos/${cursoId}/incompletos-pdf`,
  cursoSegurosPdfUrl: (cursoId) => `${API}/cursos/${cursoId}/seguros-pdf`,
  cursoSegurosRecipients: (cursoId) => req(`/cursos/${cursoId}/seguros-pdf/recipients`),
  cursoSegurosSendEmail: (cursoId, payload) =>
    req(`/cursos/${cursoId}/seguros-pdf/send-email`, { method: "POST", body: JSON.stringify(payload) }),
  sessoesImportExcel: (file, { cursoId, replace = false } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    const params = new URLSearchParams();
    if (cursoId) params.set("curso_id", String(cursoId));
    if (replace) params.set("replace", "true");
    const qs = params.toString();
    return fetch(`${API}/cursos/sessoes/import-excel${qs ? `?${qs}` : ""}`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro import"); }
      return r.json();
    });
  },

  // Edição enriquecida de curso (GET completo + PUT)
  cursoFullGet: (cursoId) => req(`/cursos/${cursoId}/full`),
  cursoFullUpdate: (cursoId, payload) =>
    req(`/cursos/${cursoId}/full`, { method: "PUT", body: JSON.stringify(payload) }),
  cursoSetEstado: (cursoId, estado) =>
    req(`/cursos/${cursoId}/estado`, {
      method: "POST", body: JSON.stringify({ estado }),
    }),
  cursoCounts: (cursoId) => req(`/cursos/${cursoId}/counts`),
  cursoEstados: () => req(`/cursos/estados`),

  // Entidade Formadora — pendentes + preenchimento rápido em massa
  cursoChecklist: (cursoId) => req(`/cursos/${cursoId}/checklist`),
  cursosEntidadeFormadoraPendentes: (apenasFuturos = true) =>
    req(`/cursos/entidade-formadora/pendentes?apenas_futuros=${apenasFuturos ? "true" : "false"}`),
  cursosEntidadeFormadoraBulk: (payload) =>
    req(`/cursos/entidade-formadora/bulk`, { method: "PUT", body: JSON.stringify(payload) }),

  // Import base completa de cursos (NOVA-BASE-BD.xlsx)
  cursosBaseImportTemplateUrl: () => `${API}/cursos/import-base/template/download`,
  cursosBaseImport: (file, { dryRun = false } = {}) => {
    const fd = new FormData();
    fd.append("file", file);
    const params = new URLSearchParams();
    if (dryRun) params.set("dry_run", "true");
    const qs = params.toString();
    return fetch(`${API}/cursos/import-base${qs ? `?${qs}` : ""}`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro import"); }
      return r.json();
    });
  },

  // Jobs Panel (cron scheduler visibility + manual run)
  jobsList: () => req("/jobs/list"),
  jobsRun: (jobId, opts = {}) => {
    const p = new URLSearchParams();
    if (opts.dry_run) p.set("dry_run", "true");
    if (opts.force) p.set("force", "true");
    const qs = p.toString();
    return req(`/jobs/run/${jobId}${qs ? `?${qs}` : ""}`, { method: "POST" });
  },
  jobsAudit: (jobId, limit = 50) => {
    const p = new URLSearchParams({ limit });
    if (jobId) p.set("job_id", jobId);
    return req(`/jobs/audit?${p.toString()}`);
  },
  jobsHistory: (jobId, limit = 100) => req(`/jobs/history/${jobId}?limit=${limit}`),

  // Manutenção de Packs (produto.Tinf_Pack)
  packsList: (params = {}) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.tipo) qs.set("tipo", params.tipo);
    if (params.activo) qs.set("activo", params.activo);
    qs.set("limit", params.limit || 100);
    qs.set("offset", params.offset || 0);
    return req(`/packs?${qs.toString()}`);
  },
  packsDropdowns: () => req(`/packs/dropdowns`),
  packGet: (id) => req(`/packs/${id}`),
  packUpdate: (id, body) => req(`/packs/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  packCreate: (body) => req(`/packs`, { method: "POST", body: JSON.stringify(body) }),
  packToggle: (id) => req(`/packs/${id}/toggle`, { method: "POST" }),
  packAddProduct: (id, idProduto) =>
    req(`/packs/${id}/products`, { method: "POST", body: JSON.stringify({ ID_Produto: idProduto }) }),
  packRemoveProduct: (id, packProdutoId) =>
    req(`/packs/${id}/products/${packProdutoId}`, { method: "DELETE" }),

  // ─── Downloads (documentos disponíveis em /my/downloads) ────────
  //
  // NB: estes endpoints vivem em `/api/backoffice/downloads/*`
  // (namespace legado do backoffice) mas usam o cookie do Admin DB.
  downloadsTipos: () => reqAbs("/api/backoffice/downloads/tipos"),
  downloadsList: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v !== "" && v !== null && v !== undefined) qs.set(k, v); });
    return reqAbs(`/api/backoffice/downloads/list${qs.toString() ? `?${qs}` : ""}`);
  },
  downloadUpload: (formData) => reqAbs("/api/backoffice/downloads/upload", {
    method: "POST", body: formData, headers: {} /* browser sets multipart boundary */,
  }),
  downloadUpdate: (id, patch) => reqAbs(`/api/backoffice/downloads/${id}`, {
    method: "PUT", body: JSON.stringify(patch),
  }),
  downloadDelete: (id) => reqAbs(`/api/backoffice/downloads/${id}`, { method: "DELETE" }),
  tipoCreate: (descricao) => reqAbs("/api/backoffice/downloads/tipos", {
    method: "POST", body: JSON.stringify({ descricao }),
  }),
  tipoUpdate: (id, patch) => reqAbs(`/api/backoffice/downloads/tipos/${id}`, {
    method: "PUT", body: JSON.stringify(patch),
  }),

  // ─── Lead Intelligence (Admin) ────────────────────────────────────
  leadIntelConfig: () => req("/lead-intelligence/config"),
  leadIntelStats: () => req("/lead-intelligence/stats"),
  leadIntelRecent: (limit = 50) => req(`/lead-intelligence/recent?limit=${limit}`),
  leadIntelReprocess: (id) => req(`/lead-intelligence/reprocess/${id}`, { method: "POST" }),
  leadIntelTimeseries: (days = 30) => req(`/lead-intelligence/timeseries?days=${days}`),

  // ─── Certificações PNL/Coaching (docs formando + avaliações) ──────
  cursoCertificacaoGet: (cursoId) => req(`/cursos/${cursoId}/certificacao`),
  cursoCertificacaoSet: (cursoId, body) =>
    req(`/cursos/${cursoId}/certificacao`, { method: "PUT", body: JSON.stringify(body) }),
  cursoEnunciadosList: (cursoId) => req(`/cursos/${cursoId}/avaliacoes/enunciados`),
  cursoEnunciadoUpload: (cursoId, file, nome) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("nome", nome || "");
    return fetch(`${API}/cursos/${cursoId}/avaliacoes/enunciados`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro upload"); }
      return r.json();
    });
  },
  cursoEnunciadoDelete: (cursoId, enunciadoId) =>
    req(`/cursos/${cursoId}/avaliacoes/enunciados/${enunciadoId}`, { method: "DELETE" }),
  cursoTestesList: (cursoId, fichaId) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/avaliacoes/testes`),
  cursoTesteUpload: (cursoId, fichaId, file, { idEnunciado, nota, observacao }) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("id_enunciado", String(idEnunciado));
    if (nota !== undefined && nota !== null && nota !== "") fd.append("nota", String(nota));
    if (observacao) fd.append("observacao", observacao);
    return fetch(`${API}/cursos/${cursoId}/inscricoes/${fichaId}/avaliacoes/testes`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro upload"); }
      return r.json();
    });
  },
  cursoDocumentosStatus: (cursoId, fichaId) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/documentos`),
  cursoDocumentoUpload: (cursoId, fichaId, tipo, file, observacao) => {
    const fd = new FormData();
    fd.append("file", file);
    if (observacao) fd.append("observacao", observacao);
    return fetch(`${API}/cursos/${cursoId}/inscricoes/${fichaId}/documentos/${tipo}`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro upload"); }
      return r.json();
    });
  },
  cursoDocumentoDelete: (cursoId, fichaId, tipo) =>
    req(`/cursos/${cursoId}/inscricoes/${fichaId}/documentos/${tipo}`, { method: "DELETE" }),

  // ─── Manutenção: Tipos de Certificação ────────────────────────────
  tiposCertificacaoList: () => req("/tipos-certificacao"),
  tipoCertificacaoCreate: (body) =>
    req("/tipos-certificacao", { method: "POST", body: JSON.stringify(body) }),
  tipoCertificacaoUpdate: (codigo, body) =>
    req(`/tipos-certificacao/${codigo}`, { method: "PUT", body: JSON.stringify(body) }),
  tipoCertificacaoDelete: (codigo) =>
    req(`/tipos-certificacao/${codigo}`, { method: "DELETE" }),
  tipoCertificacaoDocAdd: (codigo, body) =>
    req(`/tipos-certificacao/${codigo}/documentos`, { method: "POST", body: JSON.stringify(body) }),
  tipoCertificacaoDocUpdate: (docId, body) =>
    req(`/tipos-certificacao/documentos/${docId}`, { method: "PUT", body: JSON.stringify(body) }),
  tipoCertificacaoDocDelete: (docId) =>
    req(`/tipos-certificacao/documentos/${docId}`, { method: "DELETE" }),
  tipoCertificacaoDocTemplateUpload: (docId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${API}/tipos-certificacao/documentos/${docId}/template`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro upload"); }
      return r.json();
    });
  },
  tipoCertificacaoDocTemplateDelete: (docId) =>
    req(`/tipos-certificacao/documentos/${docId}/template`, { method: "DELETE" }),

  // ─── Botões CRM (chips dinâmicos do modal Evoluir Lead) ────────────
  crmBotoesList: () => req("/crm-botoes"),
  crmBotaoCreate: (body) => req("/crm-botoes", { method: "POST", body: JSON.stringify(body) }),
  crmBotaoUpdate: (id, body) => req(`/crm-botoes/${id}`, { method: "PUT", body: JSON.stringify(body) }),
  crmBotaoDelete: (id) => req(`/crm-botoes/${id}`, { method: "DELETE" }),
  crmBotoesStats: (days = 30) => req(`/crm-botoes/stats?days=${days}`),

  // ─── Assistente IA (Customer Assistant — Fase 1) ──────────────────
  aiGetConfig: () => req("/ai/config"),
  aiSetConfig: (patch) => req("/ai/config", { method: "POST", body: JSON.stringify(patch) }),
  aiSources: ({ active = "", limit = 200 } = {}) => {
    const p = new URLSearchParams();
    if (active) p.set("active", active);
    if (limit) p.set("limit", String(limit));
    const qs = p.toString();
    return req(`/ai/sources${qs ? `?${qs}` : ""}`);
  },
  aiReindex: (full = false) => req("/ai/reindex", { method: "POST", body: JSON.stringify({ full }) }),
  aiDeactivateSource: (id) => req("/ai/sources/deactivate", { method: "POST", body: JSON.stringify({ id }) }),
  aiAddUrl: (url) => req("/ai/sources/add-url", { method: "POST", body: JSON.stringify({ url }) }),
  aiRefreshSource: ({ url = "", id = "" } = {}) => req("/ai/sources/refresh", { method: "POST", body: JSON.stringify({ url, id }) }),
  aiDocuments: () => req("/ai/documents"),
  aiDeleteDocument: (doc_id) => req("/ai/documents/delete", { method: "POST", body: JSON.stringify({ doc_id }) }),
  aiDocumentDownloadUrl: (doc_id) => `${API}/ai/documents/${doc_id}/download`,
  aiUploadDocument: (file) => {
    const fd = new FormData();
    fd.append("file", file);
    return fetch(`${API}/ai/documents/upload`, {
      method: "POST", credentials: "include", body: fd,
    }).then(async (r) => {
      if (!r.ok) { const j = await r.json().catch(() => ({})); throw new Error(j.detail || "Erro ao carregar documento"); }
      return r.json();
    });
  },
  aiConversations: (limit = 50) => req(`/ai/conversations?limit=${limit}`),
  aiConversationDetail: (sessionId) => req(`/ai/conversations/${encodeURIComponent(sessionId)}`),
  aiUnanswered: ({ status = "OPEN", limit = 100 } = {}) => req(`/ai/unanswered?status=${status}&limit=${limit}`),
  aiUnansweredStatus: (id, status) => req("/ai/unanswered/status", { method: "POST", body: JSON.stringify({ id, status }) }),
  aiSalesInsights: (days = 7) => req(`/ai/sales/insights?days=${days}`),
  aiSupportInsights: (days = 7) => req(`/ai/support/insights?days=${days}`),
  aiSupportCases: (status = "") => req(`/ai/support/cases${status ? `?status=${status}` : ""}`),
  aiLeads: ({ status = "", limit = 100 } = {}) => req(`/ai/leads?status=${status}&limit=${limit}`),
  aiLeadStatus: (id, status) => req("/ai/leads/status", { method: "POST", body: JSON.stringify({ id, status }) }),
  aiRecoItems: () => req("/ai/reco-items"),
  aiRecoToggle: (id, field, value) => req("/ai/reco-items/toggle", { method: "POST", body: JSON.stringify({ id, field, value }) }),
};

// Wrapper that hits an arbitrary API path (not just `/api/admin/*`).
async function reqAbs(path, opts = {}) {
  const isFormData = opts.body instanceof FormData;
  const res = await fetch(path, {
    credentials: "include",
    headers: isFormData ? (opts.headers || {}) : { "Content-Type": "application/json", ...(opts.headers || {}) },
    ...opts,
  });
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    try { const j = await res.clone().json(); msg = j.detail || j.message || msg; } catch (e) { /* keep default */ }
    const err = new Error(msg); err.status = res.status; throw err;
  }
  return res.json();
}
